import { observable, action, makeObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import {
    GET_COMMON_REALESTATES_CONDMINIUM,
    GET_COMMON_REALESTATES__CONDMINIUM_IT2,
    GET_SELECTABLE_REALESTATES,
    GET_SELECTABLE_REALESTATES_IT2
} from "src/api/realestates";
import { CommonRealestatesCondominium } from "src/api/generated/CommonRealestatesCondominium";
import { CommonRealestatesCondominiumIT2 } from "src/api/generated/CommonRealestatesCondominiumIT2";
import { SelectableRealestates, SelectableRealestatesVariables } from "src/api/generated/SelectableRealestates";
import {
    SelectableRealestatesIT2,
    SelectableRealestatesIT2Variables
} from "src/api/generated/SelectableRealestatesIT2";

export interface RealestateCondominium {
    id: string;
    name: string;
    number: string;
    consolidationid?: string;
}

// A realestate that can be picked in the AddRealestatesModal.
// For IT2 a consolidation (Mandant) has no consolidationid, its realestates point to it.
export interface ISelectableRealestate {
    id: string;
    name: string;
    number: string;
    consolidationid?: string;
}

export enum LOADING_TYPE {
    NONE,
    REALESTATES_CONDOMINIUM,
    SELECTABLE_REALESTATES
}

export class RealestateStore {
    rootStore: RootStore;

    realestatesCondominium: RealestateCondominium[] = [];
    realestatesCondominiumIT2: RealestateCondominium[] = [];
    selectableRealestates: ISelectableRealestate[] = [];
    hasSelectableRealestatesLoadingError: boolean = false;
    private selectableRealestatesKind?: string;

    loadingType: LOADING_TYPE = LOADING_TYPE.NONE;

    error?: {
        title: string;
    } = undefined;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            realestatesCondominium: observable,
            realestatesCondominiumIT2: observable,
            selectableRealestates: observable,
            hasSelectableRealestatesLoadingError: observable,
            loadingType: observable,

            loadRealestatesCondominium: action,
            loadSelectableRealestates: action
        });
    }

    loadRealestatesCondominium = async (isIT2: boolean) => {
        try {
            if (isIT2) {
                const { data }: { data: CommonRealestatesCondominiumIT2 } =
                    await apolloClientInstance.query<CommonRealestatesCondominiumIT2>({
                        query: isIT2 ? GET_COMMON_REALESTATES__CONDMINIUM_IT2 : GET_COMMON_REALESTATES_CONDMINIUM,
                        fetchPolicy: "no-cache"
                    });

                runInAction(() => {
                    if (isIT2) {
                        this.realestatesCondominiumIT2 = data.common_realestates.flatMap((parent) => [
                            {
                                id: parent.id,
                                number: parent.number,
                                name: parent.name,
                                consolidationid: null
                            },
                            ...parent.realestates_to_consolidation.map((child) => ({
                                id: child.id,
                                number: child.number,
                                name: child.name,
                                consolidationid: child.consolidationid
                            }))
                        ]);
                    }
                });
            } else {
                const { data }: { data: CommonRealestatesCondominium } =
                    await apolloClientInstance.query<CommonRealestatesCondominium>({
                        query: isIT2 ? GET_COMMON_REALESTATES__CONDMINIUM_IT2 : GET_COMMON_REALESTATES_CONDMINIUM,
                        fetchPolicy: "no-cache"
                    });

                runInAction(() => {
                    this.realestatesCondominium = data.common_realestates.map((item) => ({
                        id: item.id,
                        number: item.number,
                        name: item.name
                    }));
                });
            }
        } catch (error) {
            console.error("Error loading realestates:", error);
        }
    };

    // The realestates of a customer hardly change during a session, so each kind is loaded only once.
    // After an error the next call loads again.
    loadSelectableRealestates = async (isIT2: boolean, isCondominium: boolean) => {
        const selectableRealestatesKind = `${isIT2}-${isCondominium}`;
        if (selectableRealestatesKind === this.selectableRealestatesKind) {
            return;
        }

        this.selectableRealestatesKind = selectableRealestatesKind;
        this.loadingType = LOADING_TYPE.SELECTABLE_REALESTATES;
        this.selectableRealestates = [];
        this.hasSelectableRealestatesLoadingError = false;

        let selectableRealestates: ISelectableRealestate[] = [];
        let hasLoadingError = false;
        try {
            selectableRealestates = isIT2
                ? await this.querySelectableRealestatesIT2(isCondominium)
                : await this.querySelectableRealestates(isCondominium);
        } catch (error) {
            console.error("Error loading selectable realestates:", error);
            hasLoadingError = true;
        }

        runInAction(() => {
            // A newer call for another kind is running, its result wins.
            if (selectableRealestatesKind !== this.selectableRealestatesKind) {
                return;
            }
            if (hasLoadingError) {
                this.selectableRealestatesKind = undefined;
            }
            this.selectableRealestates = selectableRealestates;
            this.hasSelectableRealestatesLoadingError = hasLoadingError;
            this.loadingType = LOADING_TYPE.NONE;
        });
    };

    private querySelectableRealestates = async (isCondominium: boolean): Promise<ISelectableRealestate[]> => {
        const { data } = await apolloClientInstance.query<SelectableRealestates, SelectableRealestatesVariables>({
            query: GET_SELECTABLE_REALESTATES,
            variables: { condominium: isCondominium },
            fetchPolicy: "no-cache"
        });

        return data.common_realestates.map((realestate) => ({
            id: realestate.id,
            name: realestate.name,
            number: realestate.number
        }));
    };

    // IT2 lists each consolidation (Mandant) followed by its realestates.
    private querySelectableRealestatesIT2 = async (isCondominium: boolean): Promise<ISelectableRealestate[]> => {
        const { data } = await apolloClientInstance.query<SelectableRealestatesIT2, SelectableRealestatesIT2Variables>({
            query: GET_SELECTABLE_REALESTATES_IT2,
            variables: { condominium: isCondominium },
            fetchPolicy: "no-cache"
        });

        return data.common_realestates.flatMap((consolidation) => [
            {
                id: consolidation.id,
                name: consolidation.name,
                number: consolidation.number
            },
            ...consolidation.realestates_to_consolidation.map((realestate) => ({
                id: realestate.id,
                name: realestate.name,
                number: realestate.number,
                consolidationid: realestate.consolidationid
            }))
        ]);
    };
}
